/**
 * FileService
 * Service for file management operations
 * Route: api/file
 */
import { BaseService } from '../base/BaseService';
/**
 * FileService
 * Handles file upload, download, and management operations
 */
export class FileService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FileService.DEFAULT_SERVICE_PATH,
            basePath: FileService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== UPLOAD OPERATIONS ====================
    /**
     * Generate presigned upload URL for single file
     * POST api/file/presign-upload
     */
    async presignUpload(data) {
        const response = await this.presignUploadRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate presigned upload URL');
        }
        return response.data;
    }
    /**
     * Presign upload with full response
     */
    presignUploadRaw(data) {
        const endpoint = this.getEndpoint('presign-upload', 'presign-upload');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Generate presigned upload URLs for multiple files
     * POST api/file/presign-multiple
     */
    async presignMultiple(data) {
        const response = await this.presignMultipleRaw(data);
        return response.data ?? [];
    }
    /**
     * Presign multiple with full response
     */
    presignMultipleRaw(data) {
        const endpoint = this.getEndpoint('presign-multiple', 'presign-multiple');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Confirm file upload completion
     * POST api/file/confirm
     */
    async confirmUpload(data) {
        const response = await this.confirmUploadRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to confirm upload');
        }
        return response.data;
    }
    /**
     * Confirm upload with full response
     */
    confirmUploadRaw(data) {
        const endpoint = this.getEndpoint('confirm', 'confirm');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Upload file through backend proxy (bypasses CORS issues with direct S3 upload)
     * Use this when S3 CORS is not configured or causing issues.
     * POST api/file/upload
     * @param file The file to upload
     * @param options Optional parameters (domain, recordId, folderPath)
     */
    async uploadFile(file, options) {
        const response = await this.uploadFileRaw(file, options);
        if (!response.data) {
            throw new Error(response.message || 'Failed to upload file');
        }
        return response.data;
    }
    /**
     * Upload file with full response (proxy method)
     */
    async uploadFileRaw(file, options) {
        const endpoint = this.getEndpoint('upload', 'upload');
        const params = new URLSearchParams();
        if (options?.domain)
            params.append('domain', options.domain);
        if (options?.recordId)
            params.append('recordId', options.recordId);
        if (options?.folderPath)
            params.append('folderPath', options.folderPath);
        const queryString = params.toString();
        const url = this.buildUrl(endpoint) + (queryString ? `?${queryString}` : '');
        const formData = new FormData();
        formData.append('file', file);
        return this.http.postFormData(url, formData);
    }
    /**
     * Upload file directly to S3 using presigned URL (faster, requires S3 CORS configured)
     * This is the recommended method when CORS is properly set up on your S3 bucket.
     *
     * Flow: presignUpload() → PUT to S3 → confirmUpload()
     *
     * @param file The file to upload
     * @param options Optional parameters (domain, recordId, folderPath)
     * @returns The final file URL after successful upload
     */
    async uploadFileDirect(file, options) {
        // Step 1: Get presigned upload URL
        const presigned = await this.presignUpload({
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            domain: options?.domain,
            recordId: options?.recordId,
            folderPath: options?.folderPath,
        });
        // Step 2: Upload directly to S3 using presigned URL
        // Include x-amz-acl header to make the uploaded file publicly readable
        const uploadResponse = await fetch(presigned.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
                'x-amz-acl': 'public-read',
            },
        });
        if (!uploadResponse.ok) {
            throw new Error(`Failed to upload to S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }
        // Step 3: Confirm the upload
        await this.confirmUpload({ fileId: presigned.fileId });
        return {
            fileId: presigned.fileId,
            url: presigned.finalUrl,
            storageKey: presigned.storageKey,
        };
    }
    /**
     * Smart upload - tries direct S3 upload first, optionally falls back to proxy if CORS fails.
     *
     * Note: Proxy fallback requires the backend to have the /api/file/upload endpoint deployed.
     * If proxy fallback fails, consider fixing S3 CORS configuration instead.
     *
     * @param file The file to upload
     * @param options Optional parameters (domain, recordId, folderPath, useProxyFallback)
     */
    async uploadFileAuto(file, options) {
        const useProxyFallback = options?.useProxyFallback ?? true;
        try {
            // Try direct S3 upload first (faster)
            const result = await this.uploadFileDirect(file, options);
            return { fileId: result.fileId, url: result.url, method: 'direct' };
        }
        catch (directError) {
            // If direct upload fails (likely CORS)
            console.warn('[FileService] Direct S3 upload failed:', directError);
            if (!useProxyFallback) {
                throw directError;
            }
            // Fall back to proxy
            console.info('[FileService] Attempting proxy upload fallback...');
            try {
                const result = await this.uploadFile(file, options);
                return { fileId: result.id, url: result.url, method: 'proxy' };
            }
            catch (proxyError) {
                // Both methods failed - throw with combined info
                console.error('[FileService] Proxy upload also failed:', proxyError);
                throw new Error(`File upload failed. Direct S3 upload blocked by CORS. ` +
                    `Proxy fallback error: ${proxyError instanceof Error ? proxyError.message : String(proxyError)}`);
            }
        }
    }
    // ==================== ENTITY OPERATIONS ====================
    /**
     * Get file metadata by ID
     * GET api/file/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`File ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get file by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(id);
        return this.http.get(url);
    }
    /**
     * Generate presigned download URL
     * GET api/file/{id}/download
     */
    async getDownloadUrl(id) {
        const response = await this.getDownloadUrlRaw(id);
        if (!response.data) {
            throw new Error(`Failed to get download URL for file ${id}`);
        }
        return response.data;
    }
    /**
     * Get download URL with full response
     */
    getDownloadUrlRaw(id) {
        const endpoint = this.getEndpoint('download', `${id}/download`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Delete file
     * DELETE api/file/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete file with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(id);
        return this.http.delete(url);
    }
    /**
     * Get files by domain
     * GET api/file/domain/{domain}?recordId={recordId}
     */
    async getByDomain(domain, recordId) {
        const response = await this.getByDomainRaw(domain, recordId);
        return response.data ?? [];
    }
    /**
     * Get files by domain with full response
     */
    getByDomainRaw(domain, recordId) {
        const endpoint = this.getEndpoint('domain', `domain/${domain}`);
        const url = this.buildUrlWithParams(endpoint, recordId ? { recordId } : undefined);
        return this.http.get(url);
    }
    /**
     * Get user's files
     * GET api/file/user/{userId}
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get user files with full response
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('user', `user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all variants for a specific file
     * GET api/file/{id}/variants
     */
    async getVariants(id) {
        const response = await this.getVariantsRaw(id);
        return response.data ?? [];
    }
    /**
     * Get file variants with full response
     */
    getVariantsRaw(id) {
        const endpoint = this.getEndpoint('variants', `${id}/variants`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get file with all its variants
     * GET api/file/{id}/with-variants
     */
    async getWithVariants(id) {
        const response = await this.getWithVariantsRaw(id);
        if (!response.data) {
            throw new Error(`File ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get file with variants with full response
     */
    getWithVariantsRaw(id) {
        const endpoint = this.getEndpoint('with-variants', `${id}/with-variants`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== VIEW OPERATIONS ====================
    /**
     * Get composed file view by ID (cache-first)
     * GET api/file/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`File ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get file view with full response
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get composed file view with variants by ID (cache-first)
     * GET api/file/view/{id}/with-variants
     */
    async getViewWithVariants(id) {
        const response = await this.getViewWithVariantsRaw(id);
        if (!response.data) {
            throw new Error(`File ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get view with variants with full response
     */
    getViewWithVariantsRaw(id) {
        const endpoint = this.getEndpoint('view-with-variants', `view/${id}/with-variants`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered list of file views (cache-first)
     * POST api/file/list-view
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * List view with full response
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get filtered list of file views with variants (cache-first)
     * POST api/file/list-view-with-variants
     */
    async listViewWithVariants(filter) {
        const response = await this.listViewWithVariantsRaw(filter);
        return response.data ?? [];
    }
    /**
     * List view with variants with full response
     */
    listViewWithVariantsRaw(filter) {
        const endpoint = this.getEndpoint('list-view-with-variants', 'list-view-with-variants');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get file views by user ID (cache-first)
     * GET api/file/user/{userId}/view
     */
    async getUserViews(userId) {
        const response = await this.getUserViewsRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get user views with full response
     */
    getUserViewsRaw(userId) {
        const endpoint = this.getEndpoint('user-view', `user/${userId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get file views by domain (cache-first)
     * GET api/file/domain/{domain}/view?recordId={recordId}
     */
    async getDomainViews(domain, recordId) {
        const response = await this.getDomainViewsRaw(domain, recordId);
        return response.data ?? [];
    }
    /**
     * Get domain views with full response
     */
    getDomainViewsRaw(domain, recordId) {
        const endpoint = this.getEndpoint('domain-view', `domain/${domain}/view`);
        const url = this.buildUrlWithParams(endpoint, recordId ? { recordId } : undefined);
        return this.http.get(url);
    }
    /**
     * Get current user's file views (cache-first)
     * POST api/file/my-list-view
     */
    async myListView(filter) {
        const response = await this.myListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * My list view with full response
     */
    myListViewRaw(filter) {
        const endpoint = this.getEndpoint('my-list-view', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter ?? {});
    }
    // ==================== STATISTICS OPERATIONS ====================
    /**
     * Get overall storage statistics
     * GET api/file/statistics
     */
    async getStatistics() {
        const response = await this.getStatisticsRaw();
        if (!response.data) {
            throw new Error('Failed to get storage statistics');
        }
        return response.data;
    }
    /**
     * Get storage statistics with full response
     */
    getStatisticsRaw() {
        const endpoint = this.getEndpoint('statistics', 'statistics');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get domain-specific storage statistics
     * GET api/file/statistics/domain/{domain}
     */
    async getDomainStatistics(domain) {
        const response = await this.getDomainStatisticsRaw(domain);
        if (!response.data) {
            throw new Error(`Failed to get statistics for domain: ${domain}`);
        }
        return response.data;
    }
    /**
     * Get domain statistics with full response
     */
    getDomainStatisticsRaw(domain) {
        const endpoint = this.getEndpoint('domain-statistics', `statistics/domain/${domain}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== BULK OPERATIONS ====================
    /**
     * Bulk delete files (soft delete)
     * POST api/file/bulk-delete
     */
    async bulkDelete(data) {
        const response = await this.bulkDeleteRaw(data);
        if (!response.data) {
            throw new Error('Failed to bulk delete files');
        }
        return response.data;
    }
    /**
     * Bulk delete with full response
     */
    bulkDeleteRaw(data) {
        const endpoint = this.getEndpoint('bulk-delete', 'bulk-delete');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Bulk download - generate download URLs for multiple files
     * POST api/file/bulk-download
     */
    async bulkDownload(data) {
        const response = await this.bulkDownloadRaw(data);
        if (!response.data) {
            throw new Error('Failed to generate bulk download URLs');
        }
        return response.data;
    }
    /**
     * Bulk download with full response
     */
    bulkDownloadRaw(data) {
        const endpoint = this.getEndpoint('bulk-download', 'bulk-download');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== FILE OPERATIONS ====================
    /**
     * Move file to different folder
     * POST api/file/{id}/move
     */
    async moveToFolder(id, data) {
        const response = await this.moveToFolderRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to move file ${id}`);
        }
        return response.data;
    }
    /**
     * Move file with full response
     */
    moveToFolderRaw(id, data) {
        const endpoint = this.getEndpoint('move', `${id}/move`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Advanced file search with filters
     * POST api/file/search
     */
    async search(data) {
        const response = await this.searchRaw(data);
        return response.data ?? [];
    }
    /**
     * Search with full response
     */
    searchRaw(data) {
        const endpoint = this.getEndpoint('search', 'search');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
}
FileService.DEFAULT_SERVICE_PATH = 'files';
FileService.BASE_PATH = 'api/file';
//# sourceMappingURL=FileService.js.map